<script lang="ts">
  import PageTitle from '$lib/teach/resources/PageTitle.svelte';
  import ResourcesCard from '$lib/teach/resources/ResourcesCard.svelte';
</script>

<PageTitle
  class="mb-4"
  title="Music Theory Resources"
  titleHeader="Music Theory Resources"
  description="Printable music theory handouts and digital resources from the creators of uTheory."
  seoImageUrl="/teach/printable-resources/printable-resources-seo-thumbnail.png"
  seoImageAlt="Image of the webpage listing uTheory's printable music theory resources, including handouts for Lines & Spaces, Piano & Grand Staff, Key Signatures, Solfege Hand Signs and Chromatic Hand Signs."
/>

<p class="text-gray my-2 text-lg">
  In addition to our <a class="a" href="/app/lessons">lessons</a>
  and <a class="a" href="/app/skills">practice</a> for music theory, rhythm, and ear training, uTheory
  is pleased to share these free resources for your students and classes.
</p>
<h2 id="music-theory-handouts" class="h2 mt-8">Music Fundamentals</h2>
<h3 class="text-lg text-gray mb-4 mt-2">
  Handouts for note reading, keys, intervals, and other music fundamentals.
</h3>
<div class="resources-grid">
  <ResourcesCard
    title="Lines and Spaces"
    thumbnailSrc="/teach/printable-resources/lines-and-spaces-on-the-staff-thumbnail.png"
    thumbnailAlt="Handout showing lines and spaces on the treble, bass, alto and tenor clefs."
    description="Printable lines and spaces pdf. Includes treble, bass, alto and tenor clefs."
    src="/teach/printable-resources/lines-and-spaces-on-the-staff.pdf"
  />
  <ResourcesCard
    title="Piano &amp; Grand Staff"
    thumbnailSrc="/teach/printable-resources/grand-staff-thumbnail.png"
    thumbnailAlt="Handout showing the piano keyboard and grand staff with notes labeled."
    description="Printable grand staff and piano keyboard pdf, with notes labeled from C2 - C6"
    src="/teach/printable-resources/grand-staff.pdf"
  />
  <ResourcesCard
    title="Key Signatures"
    thumbnailSrc="/teach/printable-resources/key-signatures-thumbnail.png"
    thumbnailAlt="Handout showing rules for identifying key signatures, and all key signatures in treble and bass clef."
    description="Rules for identifying key signatures, and all key signatures in treble and bass clef."
    src="/teach/printable-resources/key-signatures.pdf"
  />
  <ResourcesCard
    title="Circle of Fifths"
    thumbnailSrc="/teach/printable-resources/circle-of-fifths-thumbnail.png"
    thumbnailAlt="Handout showing the circle of fifths, with names for the major and minor keys, the number of accidentals in each key, and the keys themselves written on a grand staff."
    description="Printable Circle of Fifths, with both Major and minor keys, the order of sharps and flats, and key signatures drawn on the grand staff."
    src="/teach/printable-resources/circle-of-fifths.pdf"
  />
  <ResourcesCard
    title="Major Scale Pattern"
    thumbnailSrc="/teach/printable-resources/scale-patterns-major-thumbnail.png"
    thumbnailAlt="Handout showing the whole and half step pattern of the major scale labeled with solfege and scale degrees, then C Major and A-flat Major written on a staff and on a piano keyboard."
    description="The whole and half step pattern of the Major scale, with solfege, scale degrees, and staff and keyboard examples."
    src="/teach/printable-resources/scale-patterns-major.pdf"
  />
  <ResourcesCard
    title="Minor Scale Patterns"
    thumbnailSrc="/teach/printable-resources/scale-patterns-minor-thumbnail.png"
    thumbnailAlt="Handout showing the natural, harmonic, ascending melodic and descending melodic minor scales on a treble staff, with every whole step, half step and augmented second labeled."
    description="Natural, harmonic and melodic minor scales, with their whole steps, half steps, and alterations from Major labeled."
    src="/teach/printable-resources/scale-patterns-minor.pdf"
  />
  <ResourcesCard
    title="Solfege Hand Signs"
    thumbnailSrc="/teach/printable-resources/solfege-hand-signs-diatonic-thumbnail.png"
    thumbnailAlt="Handout showing the piano keyboard and grand staff with notes labeled."
    description="Kodaly/Curwen solfege hand signs for the Major scale."
    src="/teach/printable-resources/solfege-hand-signs-diatonic.pdf"
  />
  <ResourcesCard
    title="Chromatic Hand Signs"
    thumbnailSrc="/teach/printable-resources/solfege-hand-signs-chromatic-thumbnail.png"
    thumbnailAlt="Handout showing the piano keyboard and grand staff with notes labeled."
    description="Kodaly/Curwen solfege hand signs for the chromatic scale."
    src="/teach/printable-resources/solfege-hand-signs-chromatic.pdf"
  />

  <ResourcesCard
    title="Intervals"
    thumbnailSrc="/teach/printable-resources/intervals-thumbnail-2.png"
    thumbnailAlt="Handout showing intervals from a unison to an octave with an example, their half steps, and their inversions listed."
    description="Intervals from a unison to an octave with examples, half steps, inversions."
    src="/teach/printable-resources/intervals.pdf"
  />
  <ResourcesCard
    title="Triads"
    thumbnailSrc="/teach/printable-resources/triads-thumbnail.png"
    thumbnailAlt="Handout showing construction of triads, how to alter a triad to change its quality, and examples of triads of different qualities."
    description="How to build triads and alter their qualities."
    src="/teach/printable-resources/triads.pdf"
  />
  <ResourcesCard
    title="Triad Inversions"
    thumbnailSrc="/teach/printable-resources/triad-inversions-thumbnail.png"
    thumbnailAlt="Handout showing how to invert a triad, with examples of C Major in all of its inversions in open and close spacings."
    description="How to invert triads, with examples and explanations of the root vs. the bass of a chord."
    src="/teach/printable-resources/triad-inversions.pdf"
  />
  <ResourcesCard
    title="Seventh Chords"
    thumbnailSrc="/teach/printable-resources/seventh-chords-thumbnail.png"
    thumbnailAlt="Handout showing Major, dominant, minor, half-diminished and diminished seventh chords side by side, each with its chord symbols, the quality of its triad and seventh, and examples on F-sharp, B and A-flat."
    description="The five seventh chord qualities, their chord symbols, and how to alter each one to build the next."
    src="/teach/printable-resources/seventh-chords.pdf"
  />
</div>
<h2 id="rhythm-printables" class="h2 mt-8">Rhythm</h2>
<h3 class="text-lg text-gray mb-4 mt-2">Handouts for teaching rhythm reading and counting.</h3>
<div class="resources-grid">
  <ResourcesCard
    title="Rhythm Values in Simple Time"
    thumbnailSrc="/teach/printable-resources/note-values-simple-time-thumbnail.png"
    thumbnailAlt="Handout showing the rhythmic values from whole to thirty-second notes labeled with a beamed and not beamed version, then arranged in a grid showing division of each into the next lower rhythmic value."
    description="The rhythm tree for notes in simple times, from whole to 32nd notes."
    src="/teach/printable-resources/note-values-simple-time.pdf"
  />
  <ResourcesCard
    title="Rests in Simple Time"
    thumbnailSrc="/teach/printable-resources/rest-values-simple-time-thumbnail.png"
    thumbnailAlt="Handout showing the rest values from whole to thirty-second and labeled, then arranged in a grid showing division of each into the next lower rhythmic value."
    description="The rest rhythm tree for simple times, from whole to 32nd notes."
    src="/teach/printable-resources/rest-values-simple-time.pdf"
  />
  <ResourcesCard
    title="1e&a Counting System"
    thumbnailSrc="/teach/printable-resources/1e&a-eastman-counting-system-thumbnail.png"
    thumbnailAlt="Handout showing the 1e&a counting system for rhythms in simple and compound time."
    description="The 1-e-&-a (Eastman) counting system for simple and compound time."
    src="/teach/printable-resources/1e&a-eastman-counting-system.pdf"
  />
  <ResourcesCard
    title="Eastman (1-ti-te-ta) Counting System"
    thumbnailSrc="/teach/printable-resources/eastman-counting-system-thumbnail.png"
    thumbnailAlt="Handout showing the Eastman counting system for rhythms in simple and compound time."
    description="The 1-ti-te-ta (Eastman) counting system for simple and compound time."
    src="/teach/printable-resources/eastman-counting-system.pdf"
  />
  <ResourcesCard
    title="Takadimi Counting System"
    thumbnailSrc="/teach/printable-resources/takadimi-counting-system-thumbnail.png"
    thumbnailAlt="Handout showing the Takadimi counting system for rhythms in simple and compound time."
    description="The Takadimi counting system for simple and compound time."
    src="/teach/printable-resources/takadimi-counting-system.pdf"
  />
  <ResourcesCard
    title="Gordon Counting System"
    thumbnailSrc="/teach/printable-resources/gordon-counting-system-thumbnail.png"
    thumbnailAlt="Handout showing the Gordon counting system for rhythms in simple and compound time."
    description="The Gordon Rhythmic Solfege counting system for simple and compound time."
    src="/teach/printable-resources/gordon-counting-system.pdf"
  />
  <ResourcesCard
    title="Basic Counting in 2/4, 3/4, 4/4"
    thumbnailSrc="/teach/printable-resources/basic-counting-in-simple-time.png"
    thumbnailAlt="Handout showing the counts for quarter, half, dotted half and whole notes and rests, along with rhythmic examples for practice reading and counting."
    description="Counting quarters, halves, wholes and dotted halves in 2/4, 3/4 and 4/4 time."
    src="/teach/printable-resources/basic-counting-in-simple-time.pdf"
  />
  <ResourcesCard
    title="Eighth Note Counting in 2/4, 3/4, 4/4"
    thumbnailSrc="/teach/printable-resources/eighth-note-counting-in-simple-time.png"
    thumbnailAlt="Handout showing the counts for eighth and dotted quarter notes and rests, along with rhythmic examples for practice reading and counting."
    description="Counting eighth and dotted quarter notes and rests in 2/4, 3/4 and 4/4 time."
    src="/teach/printable-resources/eighth-note-counting-in-simple-time.pdf"
  />

  <ResourcesCard
    title="Ties in Simple Time"
    thumbnailSrc="/teach/printable-resources/ties-in-simple-time.png"
    thumbnailAlt="Handout showing counting for ties in 2/4, 3/4 and 4/4 time."
    description="Counting notes with ties in 2/4, 3/4 and 4/4 time."
    src="/teach/printable-resources/ties-in-simple-time.pdf"
  />

  <ResourcesCard
    title="Eighth-note Syncopation"
    thumbnailSrc="/teach/printable-resources/eighth-note-syncopation.png"
    thumbnailAlt="Handout showing counting for syncopated rhythms in 2/4, 3/4 and 4/4 time."
    description="Counting rhythms with eighth note syncopation in 2/4, 3/4 and 4/4 time."
    src="/teach/printable-resources/eighth-note-syncopation.pdf"
  />

  <!-- sixteenth-note-counting-in-simple-time -->
  <ResourcesCard
    title="Sixteenth Note Counting in 2/4, 3/4, 4/4"
    thumbnailSrc="/teach/printable-resources/sixteenth-note-counting-in-simple-time-thumb.png"
    thumbnailAlt="Handout showing counting for basic sixteenth note patterns in 2/4, 3/4 and 4/4 time."
    description="Counting rhythms with basic sixteenth note patterns in 2/4, 3/4 and 4/4 time."
    src="/teach/printable-resources/sixteenth-note-counting-in-simple-time.pdf"
  />

  <ResourcesCard
    title="Compound Meters: 6/8, 9/8, 12/8"
    thumbnailSrc="/teach/printable-resources/compound-meter-thumbnail.png"
    thumbnailAlt="Handout showing that the beat in compound meters is a dotted quarter note made of three eighth notes, how the time signature's numbers describe the subdivisions, and rhythms to count in 6/8, 9/8 and 12/8 time."
    description="The dotted quarter beat and its three-eighth-note subdivision, counted 1-la-li in 6/8, 9/8 and 12/8 time."
    src="/teach/printable-resources/compound-meter.pdf"
  />

  <ResourcesCard
    title="Compound Meters 2: Quarters &amp; Eighths"
    thumbnailSrc="/teach/printable-resources/compound-meter-2-thumbnail.png"
    thumbnailAlt="Handout showing quarter and eighth note patterns in compound meter labeled as skipping, on the beat, and compound snap or sarabande rhythm, followed by rhythms to count in 6/8 time."
    description="Counting mixed quarter and eighth note patterns, including skipping and compound snap rhythms, in compound meters."
    src="/teach/printable-resources/compound-meter-2.pdf"
  />

  <ResourcesCard
    title="Pickups in Compound Meters"
    thumbnailSrc="/teach/printable-resources/compound-meter-pickups-thumbnail.png"
    thumbnailAlt="Handout showing eighth note, two eighth note, and quarter note pickups in compound meter, followed by rhythms beginning with pickups to count in 6/8 time."
    description="Counting eighth note, two eighth note, and quarter note pickups in 3/8, 6/8, 9/8 and 12/8 time."
    src="/teach/printable-resources/compound-meter-pickups.pdf"
  />
</div>
<h2 id="printable-staff-paper" class="h2 mt-8">Staff Paper for Music Theory Note Taking</h2>
<h3 class="text-lg text-gray mb-4 mt-2">
  Designed for the music theory classroom, our printable manuscript/staff paper includes space to
  take notes and a piano keyboard. It's also great for use in private music lessons!
</h3>
<div class="resources-grid">
  <ResourcesCard
    title="Piano (Top) & Notes (Right)"
    thumbnailSrc="/teach/printable-resources/staff-paper-piano-top-notes-side-thumbnail.png"
    thumbnailAlt="Staff paper with a piano on the top, and space to the right for note taking."
    description="Staff paper with a piano on the top, and space to the right for note taking."
    src="/teach/printable-resources/staff-paper-piano-top-notes-side.pdf"
  />
  <ResourcesCard
    title="Piano (Bottom) & Notes (Right)"
    thumbnailSrc="/teach/printable-resources/staff-paper-piano-bottom-notes-side-thumbnail.png"
    thumbnailAlt="Staff paper with a piano on the bottom, and space to the right for note taking."
    description="Staff paper with a piano on the bottom, and space to the right for note taking."
    src="/teach/printable-resources/staff-paper-piano-bottom-notes-side.pdf"
  />

  <ResourcesCard
    title="Notes (Top) & Piano (Bottom)"
    thumbnailSrc="/teach/printable-resources/staff-paper-piano-bottom-notes-top-thumbnail.png"
    thumbnailAlt="Staff paper with space for notes on top and a piano on the bottom."
    description="Staff paper with space for notes on top and a piano on the bottom."
    src="/teach/printable-resources/staff-paper-piano-bottom-notes-top.pdf"
  />

  <ResourcesCard
    title="Notes (Middle) & Piano (Bottom)"
    thumbnailSrc="/teach/printable-resources/staff-paper-piano-bottom-notes-middle-thumbnail.png"
    thumbnailAlt="Staff paper with a space for notes in the middle, and a piano on bottom."
    description="Staff paper with a space for notes in the middle, and a piano on bottom."
    src="/teach/printable-resources/staff-paper-piano-bottom-notes-middle.pdf"
  />

  <ResourcesCard
    title="Piano (Middle) & Notes (Bottom)"
    thumbnailSrc="/teach/printable-resources/staff-paper-piano-middle-notes-bottom-thumbnail.png"
    thumbnailAlt="Staff paper with a piano in the middle, and space for notes below."
    description="Staff paper with a piano in the middle, and space for notes below."
    src="/teach/printable-resources/staff-paper-piano-middle-notes-bottom.pdf"
  />

  <ResourcesCard
    title="Staff Paper (8-staves)"
    thumbnailSrc="/teach/printable-resources/staff-paper-eight-staff-thumbnail.png"
    thumbnailAlt="Staff paper with eight large staves, good for young or beginner students."
    description="Staff paper with eight large staves, good for young or beginner students."
    src="/teach/printable-resources/staff-paper-eight-staff.pdf"
  />

  <ResourcesCard
    title="Staff Paper (10-staves)"
    thumbnailSrc="/teach/printable-resources/staff-paper-ten-staff-thumbnail.png"
    thumbnailAlt="Staff paper with ten regularly-sized staves."
    description="Staff paper with ten regularly-sized staves."
    src="/teach/printable-resources/staff-paper-ten-staff.pdf"
  />
</div>

<h2 id="online-resources" class="h2 mt-8 mb-4">Online Resources</h2>
<div class="resources-grid">
  <ResourcesCard
    title="Solfege Hand Sign Font"
    thumbnailSrc="/teach/solfege-meme-generator/font-definition-image.svg"
    thumbnailAlt="Image of the character map for the solfege hand sign font."
    description="Downloadable Kodaly/Curwen solfege hand sign font for creating your own materials."
    href="/teach/resources/kodaly-curwen-solfege-hand-signs-font"
  />

  <ResourcesCard
    title="Solfege Meme Generator"
    thumbnailSrc="/teach/solfege-meme-generator/meme-generator-thumbnail.png"
    thumbnailAlt="uTheory's Meme Generator, showing the text 'May the 4th Be With You' and the Curwen hand signs: Sol Sol Sol Do upper Sol Fa Mi Re upper Do Sol, Fa Mi Re upper Do Sol, Fa Mi Fa Re"
    description="Send a solfege meme to a friend, or use it to make creative assignments for students!"
    href="/teach/resources/memes"
  />

  <ResourcesCard
    title="Online Music Theory Lessons"
    thumbnailSrc="/teach/printable-resources/online-music-theory-lessons-thumbnail.png"
    thumbnailAlt="An image of a video lesson on uTheory showing a speaker in front of a piano, which has the first six notes of a D Major scale highlighted and labeled. To the left of the video is a table of contents for the lesson, to the top the main navigation menu for uTheory."
    description="Interactive lessons in music theory, rhythm and ear training."
    href="/app/lessons"
  />

  <ResourcesCard
    title="Online Music Theory Practice"
    thumbnailSrc="/teach/printable-resources/online-music-theory-practice-thumbnail.png"
    thumbnailAlt="An image of a practice exercise in uTheory, in which the text 'Draw the Roman Numeral D Maj: ii7' appears, and a user has written E and B in the bass clef, and G and D in the treble clef."
    description="Practice exercises for clefs, scales, intervals, triads, roman numerals, rhythm, ear training, and more."
    href="/app/skills"
  />

  <ResourcesCard
    title="Tuning Systems Synthesizer"
    thumbnailSrc="/assets/tuning-systems/tuning-systems-social.jpg"
    thumbnailAlt="An image of a piano keyboard with the notes of an F Major chord highlighted. Above, the text, 'Select a tuning system' with the options of equal temperament, just intonation (manual), just intonation (autmatic) -- which is selected, and pythagorean tuning (manual)."
    description="Experience chords in different temperaments with this just-intonation keyboard."
    href="/experiments/tuning-systems"
  />

  <ResourcesCard
    title="Pitchy Fish"
    thumbnailSrc="/assets/pitchy-fish/pitchy-fish-experiments-button.jpg"
    thumbnailAlt="A video-game-like image of a fish swimming between two logs. A comic sans, bright yellow text reads, 'Pitchy Fish! Score: 10. High Score: 0."
    description="Sing higher and lower to help the fish dodge the obstacles!"
    href="/experiments/pitchy-fish"
  />

  <ResourcesCard
    title="Solfege Sally"
    thumbnailSrc="/assets/solfege-sally/solfege-sally-seo.png"
    thumbnailAlt="The Solfege Sally game: A black circle with the words Solfege Sally in the middle, surrounded by colorful buttons labeled Do Re Mi Fa Sol La Ti Do."
    description={`Play back the tune you hear in solfege. Each time you get
              it right, one more note is added!`}
    href="/experiments/solfege-sally"
  />
</div>

<style lang="postcss">
  .resources-grid {
    @apply xs:grid xs:grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-x-6 gap-y-8;
  }
</style>
